// DropdownSelect.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/components/DropdownSelect.module.css";

// Importá como URL (no ReactComponent)
import downIcon from "../../assets/icons/down.svg";

function normalizeItems({ items, options }) {
  if (Array.isArray(items) && items.length) return items;

  // options: [{value,label}]
  if (Array.isArray(options)) {
    return options.map((o) => ({
      id: o.value,
      name: o.label,
      color: o.color, // opcional
      raw: o,
    }));
  }

  return [];
}

export default function DropdownSelect(props) {
  // API nueva (más simple)
  const {
    label,
    placeholder = "Seleccionar...",
    options,
    value,
    onChange,
    error = "",
    disabled = false,
    menuPlacement = "bottom",
  } = props;

  // API vieja (compat)
  const {
    title,
    items,
    selectedItem,
    setSelectedItem,
    isEditing,
    setIsEditing,
  } = props;

  const rootRef = useRef(null);
  const [openInner, setOpenInner] = useState(false);

  const open = typeof isEditing === "boolean" ? isEditing : openInner;
  const setOpen =
    typeof setIsEditing === "function" ? setIsEditing : setOpenInner;

  const normalizedItems = useMemo(
    () => normalizeItems({ items, options }),
    [items, options],
  );

  const selected =
    selectedItem ??
    normalizedItems.find((it) => String(it.id) === String(value)) ??
    null;

  const resolvedLabel = label ?? title ?? "";

  useEffect(() => {
    function onDocMouseDown(e) {
      if (!open) return;
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, setOpen]);

  function handleToggle() {
    if (disabled) return;
    setOpen(!open);
  }

  function handlePick(item) {
    if (disabled) return;

    // API vieja
    setSelectedItem?.(item);

    // API nueva
    onChange?.(item.id);

    setOpen(false);
  }

  const hasError = Boolean(error);
  const containerClass = [
    styles.container,
    open ? styles.open : "",
    hasError ? styles.error : "",
    disabled ? styles.disabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.wrapper} ref={rootRef}>
      {resolvedLabel ? (
        <div className={styles.label}>{resolvedLabel}</div>
      ) : null}

      <button
        type="button"
        className={containerClass}
        onClick={handleToggle}
        disabled={disabled}
      >
        <div className={styles.value}>
          {selected ? (
            selected.color ? (
              <span
                className={styles.pill}
                style={{
                  background: selected.color.bg,
                  color: selected.color.fg,
                }}
              >
                {selected.name}
              </span>
            ) : (
              selected.name
            )
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>

        <img
          src={downIcon}
          alt=""
          className={[styles.chevron, open ? styles.chevronUp : ""].join(" ")}
        />
      </button>

      {open ? (
        <div
          className={[
            styles.menu,
            menuPlacement === "top" ? styles.menuTop : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {normalizedItems.map((it) => (
            <button
              type="button"
              key={String(it.id)}
              className={styles.option}
              onClick={() => handlePick(it)}
            >
              {it.color ? (
                <span
                  className={styles.pill}
                  style={{ background: it.color.bg, color: it.color.fg }}
                >
                  {it.name}
                </span>
              ) : (
                it.name
              )}
            </button>
          ))}
        </div>
      ) : null}

      {hasError ? <div className={styles.errorText}>{error}</div> : null}
    </div>
  );
}
